import { useState, useEffect } from "react";
import { useRegister, SUGGESTED_WITHDRAWAL } from "./RegisterContext";
import { toast } from "react-toastify";

/* ── Icons ──────────────────────────────────────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconCashOut = () => <Icon d={["M12 15V3","M7 8l5-5 5 5","M5 21h14"]} />;
const IconWallet = () => <Icon d={["M21 12V7H5a2 2 0 0 1 0-4h14v4","M3 5v14a2 2 0 0 0 2 2h16v-5","M18 12a2 2 0 0 0 0 4h4v-4Z"]} />;

/* ── Scoped CSS ─────────────────────────────────────── */
const WITHDRAWAL_MODAL_CSS = `
  .wm-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    display: flex; align-items: center; justify-content: center; z-index: 200;
    backdrop-filter: blur(4px);
  }
  .wm-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 420px;
    max-width: 95vw;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    overflow: hidden;
  }
  .wm-header {
    display: flex; align-items: center; gap: 12px;
    padding: 20px 24px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .wm-header-icon {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #ff4757, #e84118);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
  }
  .wm-header-title {
    font-size: 16px; font-weight: 700; color: #fff;
  }
  .wm-header-sub {
    font-size: 12px; color: rgba(255,255,255,0.4);
    margin-top: 2px;
  }

  .wm-body {
    padding: 18px 24px;
    display: flex; flex-direction: column; gap: 14px;
  }

  .wm-balance-box {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    padding: 12px 16px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .wm-balance-box span:first-child {
    display: flex; align-items: center; gap: 8px;
    color: rgba(255,255,255,0.5); font-size: 12px;
  }
  .wm-balance-box strong {
    color: #8b8ff7; font-size: 16px;
  }

  .wm-field label {
    display: block;
    font-size: 10px; font-weight: 600; letter-spacing: 0.07em;
    color: rgba(255,255,255,0.3); text-transform: uppercase;
    margin-bottom: 4px;
  }
  .wm-input {
    padding: 10px 14px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.05);
    color: #e2e4f0; font-size: 14px;
    outline: none; width: 100%;
    transition: border-color 0.2s;
  }
  .wm-input:focus { border-color: #6366f1; }
  .wm-input::placeholder { color: rgba(255,255,255,0.2); }
  
  .wm-textarea { resize: vertical; min-height: 60px; }

  .wm-actions {
    display: flex; gap: 10px;
    padding: 16px 24px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .wm-btn-cancel {
    flex: 1;
    padding: 10px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.5); font-size: 13px; cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .wm-btn-cancel:hover { border-color: rgba(255,255,255,0.2); color: #fff; }
  .wm-btn-confirm {
    flex: 1; padding: 10px;
    border: none; border-radius: 10px;
    background: linear-gradient(135deg, #ff4757, #e84118);
    color: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: opacity 0.15s, transform 0.1s;
  }
  .wm-btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
  .wm-btn-confirm:hover:not(:disabled) { transform: scale(1.02); }
`;

export default function WithdrawalModal() {
  const { withdrawalModalVisible, setWithdrawalModalVisible, session, requestWithdrawal } = useRegister();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inject scoped styles once
  useEffect(() => {
    const styleId = "withdrawal-modal-css";
    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");
      el.id = styleId;
      el.textContent = WITHDRAWAL_MODAL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // Auto‑fill suggested amount when modal opens
  useEffect(() => {
    if (withdrawalModalVisible && session) {
      const suggested = Math.min(SUGGESTED_WITHDRAWAL, session.currentCash);
      setAmount(suggested > 0 ? suggested.toFixed(3) : "");
    }
  }, [withdrawalModalVisible, session]);

  if (!withdrawalModalVisible || !session) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.warn("Enter a valid amount");
      return;
    }
    if (amt > session.currentCash) {
      toast.warn("Amount exceeds current cash in register");
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestWithdrawal({ amount: amt, reason });
      toast.success(
        res.status === "APPROVED" ? "Withdrawal approved and recorded"
          : "Withdrawal submitted — pending manager approval"
      );
      setAmount(""); setReason("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not submit withdrawal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wm-overlay" onClick={e => e.target === e.currentTarget && setWithdrawalModalVisible(false)}>
      <div className="wm-modal">
        {/* Header */}
        <div className="wm-header">
          <div className="wm-header-icon">
            <IconCashOut />
          </div>
          <div>
            <div className="wm-header-title">Cash Withdrawal</div>
            <div className="wm-header-sub">Remove cash from the register</div>
          </div>
        </div>

        {/* Body */}
        <div className="wm-body">
          {/* Current balance */}
          <div className="wm-balance-box">
            <span><IconWallet /> Current Balance</span>
            <strong>{Number(session.currentCash).toFixed(3)} TND</strong>
          </div>

          {/* Amount field */}
          <div className="wm-field">
            <label>Amount</label>
            <div style={{ display: "flex" }}>
              <input
                type="number" step="0.001" min="0" autoFocus
                className="wm-input"
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: "none" }}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="200.000"
              />
              <span style={{
                padding: "10px 14px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderLeft: "none",
                borderRadius: "0 10px 10px 0",
                color: "rgba(255,255,255,0.3)",
                fontSize: "11px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center"
              }}>
                TND
              </span>
            </div>
          </div>

          {/* Reason */}
          <div className="wm-field">
            <label>Reason</label>
            <textarea
              className="wm-input wm-textarea"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Bank deposit, safe transfer…"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="wm-actions">
          <button type="button" className="wm-btn-cancel" onClick={() => setWithdrawalModalVisible(false)}>
            Cancel
          </button>
          <button type="submit" className="wm-btn-confirm" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Submitting…" : "Confirm Withdrawal"}
          </button>
        </div>
      </div>
    </div>
  ); 
}